// src/permissions/PermissionsProvider.js
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  ACTIONS,
  DEFAULT_PERMISSION_CONFIG,
  PERMISSIONS_DOC_PATH,
} from "./definition";

const PermissionsContext = createContext({
  can: () => false,
  hasRole: () => false,
  defaultRoute: () => "/login",
  permissions: new Set(),
  userRoles: [],
  roles: DEFAULT_PERMISSION_CONFIG.roles,
  config: DEFAULT_PERMISSION_CONFIG,
  loading: false,
  error: null,
  source: "default",
});

const normalizeConfig = (raw) => {
  if (!raw || typeof raw !== "object") return DEFAULT_PERMISSION_CONFIG;

  const roles = raw.roles && typeof raw.roles === "object" ? raw.roles : DEFAULT_PERMISSION_CONFIG.roles;
  const users = raw.users && typeof raw.users === "object" ? raw.users : {};
  const defaultRole = raw.defaultRole || DEFAULT_PERMISSION_CONFIG.defaultRole;

  return { roles, users, defaultRole };
};

const applyOverrides = (permissionsSet, overrides) => {
  if (!overrides || typeof overrides !== "object") return permissionsSet;
  Object.entries(overrides).forEach(([actionId, value]) => {
    if (value === true) permissionsSet.add(actionId);
    if (value === false) permissionsSet.delete(actionId);
  });
  return permissionsSet;
};

const buildPermissionState = (config, email) => {
  if (!email) {
    return {
      permissionsSet: new Set(),
      userRoles: [],
      roles: config.roles,
    };
  }

  const lowerEmail = email.toLowerCase();
  const userEntry = config.users?.[lowerEmail];
  const explicitRoles = Array.isArray(userEntry?.roles) ? userEntry.roles : [];
  const userRoles = explicitRoles.length > 0 ? explicitRoles : [config.defaultRole].filter(Boolean);

  const permissionsSet = new Set();
  userRoles.forEach((roleName) => {
    const roleActions = config.roles?.[roleName];
    if (Array.isArray(roleActions)) {
      roleActions.forEach((a) => permissionsSet.add(a));
    }
  });

  applyOverrides(permissionsSet, userEntry?.overrides);

  return { permissionsSet, userRoles, roles: config.roles };
};

export const PermissionsProvider = ({ user, children }) => {
  const [config, setConfig] = useState(DEFAULT_PERMISSION_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [source, setSource] = useState("default");
  const [loadedFor, setLoadedFor] = useState(null); // email we last fetched for

  useEffect(() => {
    let isMounted = true;

    const fetchConfig = async () => {
      setLoading(true);
      setError(null);
      setLoadedFor(null);

      if (!user) {
        if (!isMounted) return;
        setConfig(DEFAULT_PERMISSION_CONFIG);
        setSource("default");
        setLoading(false);
        setLoadedFor(null);
        return;
      }

      try {
        const ref = doc(db, PERMISSIONS_DOC_PATH.collection, PERMISSIONS_DOC_PATH.doc);
        const snap = await getDoc(ref);
        if (!isMounted) return;
        if (snap.exists()) {
          setConfig(normalizeConfig(snap.data()));
          setSource("remote");
          setLoadedFor(user.email.toLowerCase());
        } else {
          setConfig(DEFAULT_PERMISSION_CONFIG);
          setSource("default");
          setLoadedFor(user.email.toLowerCase());
        }
      } catch (err) {
        if (!isMounted) return;
        setError(err?.message || "Failed to load permissions");
        setConfig(DEFAULT_PERMISSION_CONFIG);
        setSource("default");
        setLoadedFor(user.email.toLowerCase());
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchConfig();
    return () => {
      isMounted = false;
    };
  }, [user]);

  const { permissionsSet, userRoles, roles } = useMemo(
    () => buildPermissionState(config, user?.email),
    [config, user?.email]
  );

  const can = useMemo(
    () => (actionId) => permissionsSet.has(actionId),
    [permissionsSet]
  );

  const hasRole = useMemo(
    () => (roleName) => userRoles.includes(roleName),
    [userRoles]
  );

  const defaultRoute = useMemo(() => {
    if (can(ACTIONS.NAV_VIEW_PROJECTS)) return "/";
    if (can(ACTIONS.NAV_VIEW_TASKS)) return "/tasks";
    if (can(ACTIONS.NAV_VIEW_SCHEDULE)) return "/schedule";
    if (can(ACTIONS.NAV_VIEW_CLIENTS)) return "/clients";
    return "/login";
  }, [can]);

  const value = useMemo(
    () => ({
      can,
      hasRole,
      defaultRoute,
      permissions: permissionsSet,
      userRoles,
      roles,
      config,
      loading,
      error,
      source,
      loadedFor,
    }),
    [can, hasRole, defaultRoute, permissionsSet, userRoles, roles, config, loading, error, source, loadedFor]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return ctx;
};
